import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  useDeleteApiAiProviderConnectionsId,
} from "@/lib/api/generated/providers/providers";
import { postApiAiProviderConnectionsOpenaiCodexImportLocal } from "@/lib/api/generated/provider-o-auth/provider-o-auth";
import type { TenantAiProviderConnectionRecordDto } from "@/lib/api/generated/control-api.schemas";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import type { AiProviderMetadata } from "./provider-metadata.js";

export function OpenAICodexConnectCard(params: {
  tenantId: string;
  metadata: AiProviderMetadata;
  existing: TenantAiProviderConnectionRecordDto | null;
  modelCount: number;
}) {
  const { tenantId, metadata, existing, modelCount } = params;
  const queryClient = useQueryClient();
  const importMutation = useMutation({
    mutationFn: (data: null) => postApiAiProviderConnectionsOpenaiCodexImportLocal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantAiProviderConnections(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantModelCatalog(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });
  const deleteMutation = useDeleteApiAiProviderConnectionsId({
    mutation: {
      onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantAiProviderConnections(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantModelCatalog(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
    },
  });
  const status = existing?.status ?? "disconnected";
  const isBusy = importMutation.isPending || deleteMutation.isPending;
  const connectLabel = status === "active" ? "Re-import Codex login" : "Use Codex from this machine";

  async function handleImport() {
    try {
      await importMutation.mutateAsync(null);
      toast.success("Local Codex login imported for this tenant");
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to import local Codex login"));
    }
  }

  async function handleDisconnect() {
    if (!existing) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({
        id: existing.id,
      });
      toast.success("ChatGPT disconnected");
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to disconnect ChatGPT"));
    }
  }

  return (
    <Card className="h-full">
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-base">{metadata.label}</CardTitle>
            <CardDescription>{metadata.description}</CardDescription>
          </div>
          <StatusBadge status={status} />
        </div>
        <div
          className="flex flex-wrap gap-2 text-[11px]"
          style={{ color: "var(--muted-foreground)" }}
        >
          <Badge variant="outline">OAuth</Badge>
          <Badge variant="outline">
            {modelCount} catalog model{modelCount === 1 ? "" : "s"}
          </Badge>
          {importMutation.isPending ? <Badge variant="outline">Importing…</Badge> : null}
          {existing?.lastValidatedAt ? (
            <Badge variant="outline">Validated {formatShortDate(existing.lastValidatedAt)}</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div
          className="rounded-[var(--radius-sm)] border px-3 py-3"
          style={{
            borderColor: "var(--border)",
            background: "color-mix(in srgb, var(--surface) 88%, var(--bg-accent))",
          }}
        >
          <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
            Recommended onboarding
          </div>
          <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
            Import the local Codex login already present on this machine. The control plane stores
            a tenant-scoped OpenAI Codex credential in Postgres, and tenant runtimes hydrate that
            credential for conversations, channels, and agents.
          </p>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-xs" style={{ color: "var(--muted-foreground)" }}>
            <li>Sign in with Codex on this machine if you have not already done it in the TUI.</li>
            <li>Click the import button below.</li>
            <li>The tenant runtime receives that credential from the control plane.</li>
          </ol>
        </div>

        <ReadOnlyField label="Provider">{metadata.providerId}</ReadOnlyField>
        <ReadOnlyField label="Auth profile">{existing?.profileId ?? "Will be created during import"}</ReadOnlyField>
        {existing?.metadata?.accountId ? (
          <ReadOnlyField label="ChatGPT account ID">
            {String(existing.metadata.accountId)}
          </ReadOnlyField>
        ) : null}

        {((importMutation.error instanceof Error ? importMutation.error.message : null) ?? existing?.lastError) ? (
          <div
            className="rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
            style={{
              background: "color-mix(in srgb, var(--danger) 10%, transparent)",
              borderColor: "color-mix(in srgb, var(--danger) 40%, var(--border))",
              color: "var(--text-strong)",
            }}
          >
            {(importMutation.error instanceof Error ? importMutation.error.message : null) ??
              existing?.lastError}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void handleImport()} disabled={isBusy}>
            {connectLabel}
          </Button>
          {existing ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void handleDisconnect()}
              disabled={isBusy}
            >
              Disconnect
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function ReadOnlyField(params: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {params.label}
      </span>
      <div
        className="rounded-[var(--radius-sm)] border px-3 py-2 font-mono text-sm"
        style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
      >
        {params.children}
      </div>
    </div>
  );
}

function formatShortDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}
